import React, { useEffect, useRef, useState } from 'react';
import { Icon } from './ui';

/**
 * Datums- und Uhrzeitfelder, die unabhängig von der Gerätesprache deutsch anzeigen (TT.MM.JJJJ, 24 Stunden).
 * Native `type="date"`/`"time"` richten sich nach der Browsersprache und zeigen auf englischen Geräten
 * 10/05/2026 bzw. AM/PM. Werte bleiben wie bei den nativen Feldern: `yyyy-MM-dd` und `HH:mm`.
 */

const zweistellig = (n: number) => String(n).padStart(2, '0');

/** `yyyy-MM-dd` → `TT.MM.JJJJ` */
export const datumDeutsch = (iso: string) => {
  const [j, m, t] = iso.split('-');
  return j && m && t ? `${t}.${m}.${j}` : iso;
};

/**
 * Liest `5.10.2026` oder `05102026`; mit `kurz` auch `5.10.26` und `5.10.` (laufendes Jahr).
 * Liefert `yyyy-MM-dd` oder null.
 */
export const parseDatum = (text: string, kurz = true): string | null => {
  const t = text.trim();
  const m = t.match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})$/)
    ?? t.match(/^(\d{2})(\d{2})(\d{4})$/)
    ?? (kurz ? t.match(/^(\d{1,2})\.(\d{1,2})\.?(\d{2})?$/) : null);
  if (!m) return null;
  const tag = Number(m[1]);
  const monat = Number(m[2]);
  let jahr = m[3] ? Number(m[3]) : new Date().getFullYear();
  if (jahr < 100) jahr += 2000;
  const d = new Date(jahr, monat - 1, tag);
  if (d.getFullYear() !== jahr || d.getMonth() !== monat - 1 || d.getDate() !== tag) return null;
  return `${jahr}-${zweistellig(monat)}-${zweistellig(tag)}`;
};

/** Liest `8:30`, `8.30` oder `0830`; mit `kurz` auch `8` und `830`. Liefert `HH:mm` oder null. */
export const parseUhrzeit = (text: string, kurz = true): string | null => {
  const t = text.trim();
  let h: number, min: number;
  const mitTrenner = t.match(/^(\d{1,2})[:.](\d{2})$/);
  if (mitTrenner) {
    h = Number(mitTrenner[1]);
    min = Number(mitTrenner[2]);
  } else if (/^\d{4}$/.test(t) || (kurz && /^\d{3}$/.test(t))) {
    h = Number(t.slice(0, -2));
    min = Number(t.slice(-2));
  } else if (kurz && /^\d{1,2}$/.test(t)) {
    h = Number(t);
    min = 0;
  } else {
    return null;
  }
  if (h > 23 || min > 59) return null;
  return `${zweistellig(h)}:${zweistellig(min)}`;
};

interface FeldProps {
  value: string;
  onChange: (wert: string) => void;
  required?: boolean;
  disabled?: boolean;
  id?: string;
  /** Rahmen, Abstand, Breite – wie bisher am nativen Feld */
  className?: string;
  'aria-label'?: string;
}

/**
 * Gemeinsame Logik: freier Text, der bei gültiger Eingabe sofort übernommen und beim Verlassen
 * normalisiert (oder auf den letzten gültigen Wert zurückgesetzt) wird.
 */
const useTextfeld = (
  value: string,
  onChange: (w: string) => void,
  anzeigen: (w: string) => string,
  parsen: (t: string, kurz?: boolean) => string | null,
) => {
  const [text, setText] = useState(value ? anzeigen(value) : '');
  const fokus = useRef(false);

  // Von außen gesetzte Werte (z. B. „Jetzt“-Knopf) übernehmen, solange nicht getippt wird
  useEffect(() => {
    if (!fokus.current) setText(value ? anzeigen(value) : '');
  }, [value, anzeigen]);

  return {
    value: text,
    onFocus: () => { fokus.current = true; },
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
      setText(e.target.value);
      // Beim Tippen nur eindeutig vollständige Eingaben übernehmen („8“ könnte noch „8:30“ werden)
      const wert = parsen(e.target.value, false);
      if (wert && wert !== value) onChange(wert);
      if (e.target.value.trim() === '' && value) onChange('');
    },
    onBlur: () => {
      fokus.current = false;
      const wert = parsen(text);
      if (wert) {
        setText(anzeigen(wert));
        if (wert !== value) onChange(wert);
      } else if (text.trim() !== '') {
        setText(value ? anzeigen(value) : '');
      }
    },
  };
};

const ohneAnzeige = (w: string) => w;

export const UhrzeitFeld: React.FC<FeldProps> = ({ value, onChange, required, disabled, id, className = '', ...rest }) => {
  const feld = useTextfeld(value, onChange, ohneAnzeige, parseUhrzeit);
  return (
    <input
      {...feld}
      {...rest}
      id={id}
      type="text"
      inputMode="numeric"
      autoComplete="off"
      placeholder="HH:MM"
      maxLength={5}
      required={required}
      disabled={disabled}
      className={`${/(^|\s)(w-|flex-1)/.test(className) ? '' : 'w-[5.5rem] '}tabular-nums ${className}`}
    />
  );
};

export const DatumFeld: React.FC<FeldProps & { min?: string }> = ({ value, onChange, required, disabled, id, min, className = '', ...rest }) => {
  const feld = useTextfeld(value, onChange, datumDeutsch, parseDatum);
  const picker = useRef<HTMLInputElement>(null);

  const kalenderOeffnen = () => {
    const el = picker.current;
    if (!el) return;
    try { el.showPicker(); } catch { el.focus(); el.click(); }
  };

  return (
    <span className={`relative inline-flex items-center gap-1 focus-within:ring-2 focus-within:ring-brand-500 ${disabled ? 'bg-gray-100' : 'bg-white'} ${className}`}>
      <input
        {...feld}
        {...rest}
        id={id}
        type="text"
        inputMode="numeric"
        autoComplete="off"
        placeholder="TT.MM.JJJJ"
        maxLength={10}
        required={required}
        disabled={disabled}
        className="w-[6.5rem] min-w-0 flex-1 bg-transparent outline-none tabular-nums"
      />
      <button
        type="button"
        onClick={kalenderOeffnen}
        disabled={disabled}
        tabIndex={-1}
        title="Kalender öffnen"
        className="flex-shrink-0 text-gray-400 hover:text-brand-600 disabled:opacity-50"
      >
        <Icon.Kalender className="h-4 w-4" />
      </button>
      {/* Unsichtbares natives Feld nur für die Kalenderauswahl des Geräts */}
      <input
        ref={picker}
        type="date"
        value={value}
        min={min}
        onChange={(e) => e.target.value && onChange(e.target.value)}
        tabIndex={-1}
        aria-hidden
        className="pointer-events-none absolute bottom-0 right-0 h-0 w-0 opacity-0"
      />
    </span>
  );
};
